/**
 * Nameless scenes (prototype). A scene's only real name is a custom one the
 * writer typed; the auto-generated `scene-xxxxx.md` file carries none. With no
 * name, the outline shows the scene's opening words (live, muted italic), and
 * the Storyboard shows its synopsis. Nothing is derived from position, so
 * reordering never renames anything.
 *
 * Pure: callers hand in the scene's file text (Markdown or `.zwdoc`) or an
 * already-derived opening; reading files is the effects layer's job
 * (`useSceneOpeningsStore`).
 */
import { displayFileName } from "./fileNames";
import { assertNever } from "./assertNever";
import { just, matchMaybe, nothing, type Maybe } from "./maybe";
import { stripSceneFrontmatterLayers } from "./platform/sceneFrontmatter";
import { proseForWordCount } from "./wordCount";
import { isZwDocText, parseZwDoc } from "./zwdoc/format";
import { projectPlainText } from "./zwdoc/plainText";
import {
  isDefaultSceneFileName,
  type ZwChapter,
  type ZwScene,
} from "./zwConfig";

/** How many words of prose stand in for a missing name. */
export const SCENE_OPENING_WORDS = 8;

/** Only scan this much of a scene for its opening words. */
const OPENING_SCAN_CHARS = 2000;

/** What we know about a scene's opening prose. `unknown` = not read (yet), or
 * unreadable on this platform; distinct from a scene that is truly empty. */
export type SceneOpening =
  { kind: "unknown" } | { kind: "empty" } | { kind: "text"; text: string };

export const unknownOpening: SceneOpening = { kind: "unknown" };

/** How a scene is labelled wherever it's listed. */
export type SceneLabel =
  | { kind: "named"; text: string }
  | { kind: "opening"; text: string }
  | { kind: "empty" }
  | { kind: "untitled" };

export const EMPTY_SCENE_LABEL = "Empty scene";
export const UNTITLED_SCENE_LABEL = "Untitled scene";

/** The writer's own name for a scene, or Nothing for an auto-named file. */
export function sceneCustomName(scene: ZwScene): Maybe<string> {
  return isDefaultSceneFileName(scene.file)
    ? nothing
    : just(displayFileName(scene.file));
}

/** Markdown → plain prose: no frontmatter, comment markup, headings, list
 * bullets, emphasis, links/images or HTML tags. */
function plainProse(markdown: string): string {
  const { body } = stripSceneFrontmatterLayers(markdown);
  return proseForWordCount(body.slice(0, OPENING_SCAN_CHARS))
    .replace(/<!--[\s\S]*?(-->|$)/g, " ")
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/<[^>]+>/g, " ")
    .replace(/^\s{0,3}(#{1,6}|>|[-*+]|\d+[.)])\s+/gm, "")
    .replace(/[*_~`]+/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** `.zwdoc` → plain prose via the shared projection (same path as word count). */
function zwdocProse(raw: string): string {
  const parsed = parseZwDoc(raw);
  if (parsed.ok === false) return "";
  return projectPlainText(parsed.value.doc)
    .text.slice(0, OPENING_SCAN_CHARS)
    .replace(/\s+/g, " ")
    .trim();
}

/** First `maxWords` words of a scene's prose, ellipsized when there's more.
 * Accepts legacy Markdown or `.zwdoc` file text. */
export function sceneOpeningFromDoc(
  markdown: string,
  maxWords: number = SCENE_OPENING_WORDS,
): SceneOpening {
  const prose = isZwDocText(markdown)
    ? zwdocProse(markdown)
    : plainProse(markdown);
  const words = prose.split(" ").filter((word) => word !== "");
  if (words.length === 0) return { kind: "empty" };
  const head = words.slice(0, maxWords).join(" ");
  const text =
    words.length > maxWords ? `${head.replace(/[,;:.!?—–-]+$/, "")}…` : head;
  return { kind: "text", text };
}

/** Plain-text rendering of an opening (outline rows that never use a title).
 * Body prose wins; when the body is empty or unread, the synopsis stands in. */
export function sceneOpeningText(
  opening: SceneOpening,
  synopsis: string = "",
): string {
  if (opening.kind === "text") return opening.text;
  const fromSynopsis = sceneOpeningFromDoc(synopsis);
  if (fromSynopsis.kind === "text") return fromSynopsis.text;
  switch (opening.kind) {
    case "empty":
      return EMPTY_SCENE_LABEL;
    case "unknown":
      return UNTITLED_SCENE_LABEL;
    default:
      return assertNever(opening);
  }
}

/** Outline / title-bar label: custom name first, else the opening words. */
export function sceneLabel(scene: ZwScene, opening: SceneOpening): SceneLabel {
  if (isDefaultSceneFileName(scene.file) === false) {
    return { kind: "named", text: displayFileName(scene.file) };
  }
  switch (opening.kind) {
    case "text":
      return { kind: "opening", text: opening.text };
    case "empty":
      return { kind: "empty" };
    case "unknown":
      return { kind: "untitled" };
    default:
      return assertNever(opening);
  }
}

/** Plain-text rendering of a label (tooltips, aria, announcements). */
export function sceneLabelText(label: SceneLabel): string {
  switch (label.kind) {
    case "named":
    case "opening":
      return label.text;
    case "empty":
      return EMPTY_SCENE_LABEL;
    case "untitled":
      return UNTITLED_SCENE_LABEL;
    default:
      return assertNever(label);
  }
}

/** True when the label stands in for a missing name (render it muted). */
export function isPlaceholderLabel(label: SceneLabel): boolean {
  return label.kind !== "named";
}

/**
 * Plain-text name for a scene in pickers and other lists: custom name, else a
 * short synopsis snippet, else body opening words (from `useSceneOpeningsStore`
 * when passed), else "Untitled scene". Synopsis wins over body so pickers stay
 * stable when the writer has filled the card; the outline still prefers body
 * via `sceneOpeningText`. Never positional.
 */
export function sceneListName(
  scene: ZwScene,
  opening: SceneOpening = unknownOpening,
): string {
  return matchMaybe(sceneCustomName(scene), {
    just: (name) => name,
    nothing: () => {
      const fromSynopsis = sceneOpeningFromDoc(scene.synopsis);
      if (fromSynopsis.kind === "text") return fromSynopsis.text;
      return sceneOpeningText(opening);
    },
  });
}

/** Chapter synopsis is retired (prototype): a chapter with exactly one scene
 * shows that scene's synopsis wherever the chapter's used to show. Nothing
 * for 0 or 2+ scenes, where the chapter has no synopsis of its own. */
export function chapterSoleScene(chapter: ZwChapter): Maybe<ZwScene> {
  return chapter.scenes.length === 1 ? just(chapter.scenes[0]!) : nothing;
}
